import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BrailleSpinner } from "./primitive/BrailleSpinner";
import { VoiceAuroraOrb, type OrbActivity } from "../voice/VoiceAuroraOrb";

const THINKING_PHRASES = [
  "Surveying the arena...",
  "Running risk models...",
  "Reviewing the legion's positions...",
  "Consulting the Consilium...",
  "Analyzing macro data...",
  "Checking volatility surface...",
  "Evaluating sentiment...",
  "Processing market signals...",
  "Cross-referencing events...",
  "Calculating exposure...",
  "Mapping liquidity pockets...",
  "Tracking implied vol drift...",
  "Pricing catalyst risk...",
  "Calibrating entry zones...",
  "Stress-testing conviction...",
];

interface FintheonThinkingIndicatorProps {
  isThinking: boolean;
  thinkingContent?: string;
  agentName?: string;
  phraseOverride?: string;
  orbActivity?: OrbActivity;
}

export function FintheonThinkingIndicator({
  isThinking,
  thinkingContent,
  agentName,
  phraseOverride,
  orbActivity,
}: FintheonThinkingIndicatorProps) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isThinking) return;
    setPhraseIndex(0);
    const interval = window.setInterval(() => {
      setPhraseIndex((value) => (value + 1) % THINKING_PHRASES.length);
    }, 2000);
    return () => window.clearInterval(interval);
  }, [isThinking]);

  if (!isThinking) return null;

  const displayLine = phraseOverride ?? THINKING_PHRASES[phraseIndex];
  const showExpand = Boolean(thinkingContent && thinkingContent.trim());

  return (
    <div
      className="w-full overflow-hidden rounded-xl fintheon-thinking-container-borderless"
      aria-label="Harper thinking trail"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:text-[var(--fintheon-accent)]"
      >
        <div className="flex h-6 w-6 shrink-0 items-center justify-center">
          {orbActivity ? (
            <VoiceAuroraOrb activity={orbActivity} size={16} />
          ) : (
            <BrailleSpinner size={14} color="var(--fintheon-accent)" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="truncate whitespace-nowrap text-[12px] font-medium"
              style={{ color: "var(--fintheon-accent)" }}
            >
              {displayLine}
            </span>
            {agentName ? (
              <span className="shrink-0 whitespace-nowrap text-[10px] text-zinc-500">
                ({agentName})
              </span>
            ) : null}
          </div>
        </div>
        {showExpand ? (
          <span
            className="ml-0.5 -mt-0.5 inline-flex h-4 items-center gap-1 text-[10px] text-zinc-500 transition-colors"
            aria-hidden="true"
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        ) : null}
      </button>
      {showExpand && expanded ? (
        <div className="border-t border-[var(--fintheon-accent)]/10 px-3 py-2">
          <div className="max-h-[180px] overflow-y-auto text-[11px] leading-relaxed text-zinc-400 whitespace-pre-wrap">
            {thinkingContent}
          </div>
        </div>
      ) : null}
    </div>
  );
}
